import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Typography } from 'src/typography';
import { observer } from 'mobx-react';

import { CREDIT_CARD_BY_PHONE, PAYMENT_OPTION_ICONS, PAYMENT_OPTION_ICON_COMPONENTS } from 'src/payments/constants';
import useDispensaryPaymentMethods from 'src/hooks/use-dispensary-payment-methods';
import useStores from 'shared/hooks/use-stores';
import { Image, RadioGroup } from 'shared/components';

import { categorizePaymentOptions, isTippableMethod } from 'src/checkout/components/sections/payment/shared/utils';
import { PaymentBanner } from 'src/checkout/components/sections/payment/shared/banner';

export const PAY_BY_PHONE_MESSAGE = 'A member of our staff will contact you by phone to take your payment.';
export const INSTORE_PAYMENT_OPTIONS_TEST_ID = 'instore-payment-options';

const PaymentOptionLabel = ({
  label,
  value,
  sublabel,
}: {
  label: string;
  value: string;
  sublabel?: string;
}): JSX.Element => {
  const IconComponent = PAYMENT_OPTION_ICON_COMPONENTS[value];
  return (
    <PaymentOption>
      {IconComponent ? (
        <IconComponent aria-hidden width={27} height={27} style={{ flexShrink: 0 }} />
      ) : (
        <Image
          alt={`${typeof value === 'string' ? String(value) : ''} payment icon`}
          src={PAYMENT_OPTION_ICONS[value]}
          flexShrink={0}
        />
      )}
      <PaymentOptionText size='medium' hasSublabel={!!sublabel}>
        <strong>{label}</strong>
        {sublabel}
      </PaymentOptionText>
    </PaymentOption>
  );
};

export const InstorePaymentOption = observer(
  ({ containerStyles }: { containerStyles?: React.CSSProperties }): JSX.Element => {
    const { Cart } = useStores();
    const { setPaymentMethod, removeTip, setPaymentMethodId, paymentMethod, dispensary, orderType } = Cart;
    // useDispensaryPaymentMethods references the Mobx cart stores
    // computed order property making it change the response on every store update
    const paymentMethods = useDispensaryPaymentMethods();

    // This enables us to only re-render when the payment methods change
    const paymentMethodsKey = useMemo(() => {
      const enabledPaymentOptions = paymentMethods?.filter(({ disabled }) => !disabled) ?? [];
      if (enabledPaymentOptions.length === 0) {
        return 'none';
      }
      return enabledPaymentOptions
        .map(({ value }) => value)
        .filter(Boolean)
        .sort()
        .join(',');
    }, [paymentMethods]);

    const memoizedPaymentOptions = useMemo(() => {
      const { offlinePaymentOptions: paymentOptions } = categorizePaymentOptions(paymentMethods ?? []);
      return paymentOptions;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- key represents paymentMethods content
    }, [paymentMethodsKey]);

    const payByPhoneSelected = paymentMethod === CREDIT_CARD_BY_PHONE;

    const onPaymentSelect = useCallback(
      (value: string): void => {
        setPaymentMethod(value);
        // Clear tip when switching to a payment method that doesn't support tipping
        if (!isTippableMethod(value, orderType, dispensary)) {
          removeTip();
        }
        setPaymentMethodId(null);
      },
      [setPaymentMethod, removeTip, setPaymentMethodId, orderType, dispensary]
    );

    const radioOptions = useMemo(
      () =>
        memoizedPaymentOptions.map((option) => ({
          ...option,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          LabelComponent: () => <PaymentOptionLabel {...option} />,
        })),
      [memoizedPaymentOptions]
    );

    const radioGroupElement = useMemo(
      () => (
        <RadioGroup
          options={radioOptions}
          display='block'
          checked={paymentMethod}
          onChange={onPaymentSelect}
          mb='12px'
          name='paymentType'
          required
        />
      ),
      [radioOptions, paymentMethod, onPaymentSelect]
    );

    return (
      <PaymentContent data-testid={INSTORE_PAYMENT_OPTIONS_TEST_ID} style={containerStyles}>
        {payByPhoneSelected && <PaymentBanner title='IMPORTANT:' description={PAY_BY_PHONE_MESSAGE} />}
        <PaymentCardText weight='semi' size='large'>
          Select payment type:
        </PaymentCardText>
        <PaymentOptionsContent>{radioGroupElement}</PaymentOptionsContent>
      </PaymentContent>
    );
  }
);

const PaymentContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  align-self: stretch;
  border: 1px solid var(--dcm-checkout-payment-tab-border-color, ${({ theme }) => theme.colors.grey[85]});
  padding: ${({ theme }) => theme.spaces[4]};
  border-radius: var(--dcm-radius-md);
  max-width: 390px;
  background-color: var(--dcm-checkout-payment-content-bg, ${({ theme }) => theme.colors.white});
`;

const PaymentCardText = styled(Typography.Body)`
  align-self: stretch;
  color: var(--dcm-checkout-section-heading-color, ${({ theme }) => theme.colors.primaryBlack});
  line-height: 165%;
  font-size: 14px;
`;

const PaymentOptionsContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spaces[3]};
  align-self: stretch;

  label:last-child {
    margin-bottom: 0;
  }
`;

const PaymentOption = styled.div`
  align-items: center;
  gap: ${({ theme }) => theme.spaces[2]};
  display: flex;
  margin-top: -6px;
  color: var(--dcm-checkout-payment-icon-color, #a3b2c1);
`;

const PaymentOptionText = styled<{ hasSublabel?: boolean }>(Typography.Body)`
  color: var(--dcm-checkout-text-color, #646d72);
  line-height: 20px;
  font-family: var(--dcm-font-secondary, 'Proxima Nova');
  & strong {
    font-weight: 600;
    font-family: var(--dcm-font-primary, 'Matter');
    ${({ hasSublabel }) => hasSublabel && 'margin-right: 4px;'}
  }
`;
